import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/build-back-better-detail.webp";
import HeroImgMobile       from "../../assets/images/blogs/build-back-better-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";

/* ─────────────────────────────────────────────
   BuildBackBetterWithEnergyEfficiencyImprovements.jsx
   Prefix      : bb-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title  : CENTERED (2 lines)
   - bb-quote    : italic opening para (attribution quote)
   - bb-subhead  : standard subhead "Survive and Thrive!"
   - No inline images, no tweets, no bullets
   - Newsletter  : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .bb-wrap *, .bb-wrap *::before, .bb-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .bb-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .bb-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .bb-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .bb-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .bb-fade-up.bb-in { opacity: 1; transform: translateY(0); }
  .bb-delay-1 { transition-delay: 0.08s; }
  .bb-delay-2 { transition-delay: 0.18s; }
  .bb-delay-3 { transition-delay: 0.28s; }
  .bb-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .bb-hero { width: 100%; background: #1A1A1A; }
  .bb-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .bb-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .bb-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .bb-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .bb-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  /* italic opening attribution quote */
  .bb-quote {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* regular paragraph */
  .bb-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
   
  }
     .bb-para:last-child { margin-bottom: 0px;}

  /* standard subhead — "Survive and Thrive!" */
  .bb-subhead {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    line-height: 160%;
    margin-bottom: 14px;
  
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .bb-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .bb-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
   
    object-position: center;
    z-index: 0;
  }
  .bb-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .bb-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .bb-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .bb-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .bb-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .bb-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .bb-newsletter__input::placeholder { color: #FFFFFF; }
  .bb-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .bb-newsletter__btn:hover { background: #5e2e90; }
  .bb-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .bb-newsletter__btn:hover .bb-newsletter__btn-arrow { transform: translateX(4px); }
  .bb-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .bb-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .bb-inner { padding-left: 32px; padding-right: 32px; }
    .bb-hero__img-wrap img { max-height: 350px; }
    .bb-hero__title-bar { padding: 24px 0px 20px; }
    .bb-hero__title { font-size: 24px; }
    .bb-body-section { padding: 40px 0 48px; }
    .bb-para { margin-bottom: 18px; font-size: 15px; }
    .bb-quote { font-size: 15px; margin-bottom: 18px; }
    .bb-subhead { font-size: 18px; margin-top: 28px; margin-bottom: 10px; }

    .bb-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .bb-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .bb-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .bb-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .bb-newsletter__heading { font-size: 24px; }
    .bb-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .bb-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .bb-newsletter__input { border-radius: 5px; height: 44px; }
    .bb-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .bb-inner { padding-left: 16px; padding-right: 16px; }
    .bb-hero__title-bar { padding: 30px 0px; }
    .bb-body-section { padding: 30px 0 30px; }
    .bb-hero__img-wrap img { max-height: 366px; width: 100%; }
    .bb-hero__title { font-size: 26px; line-height: 120%; }
    .bb-para { font-size: 14px; margin-bottom: 14px; }
    .bb-quote { font-size: 14px; margin-bottom: 14px; }
    .bb-subhead { margin-top: 30px; margin-bottom: 14px; font-size: 18px; }

    .bb-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .bb-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .bb-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .bb-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .bb-newsletter__heading { font-size: 30px; line-height: 120%; }
    .bb-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .bb-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .bb-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .bb-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".bb-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("bb-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="bb-newsletter">
      {!isMobile && (
        <>
          <img className="bb-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="bb-newsletter__overlay" />
        </>
      )}
      <div className="bb-newsletter__inner">
        {isMobile && (
          <img className="bb-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="bb-newsletter__content">
          <h2 className="bb-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="bb-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="bb-newsletter__form">
            <input
              className="bb-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="bb-newsletter__btn">
              Subscribe
              <span className="bb-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function BuildBackBetterWithEnergyEfficiencyImprovements() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="bb-wrap">

        {/* HERO — centered title */}
        <section className="bb-hero">
          <div className="bb-hero__title-bar">
            <div className="bb-inner">
              <h1 className="bb-hero__title">Build back better – with energy efficiency improvements</h1>
            </div>
          </div>
          <div className="bb-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="Build Back Better Energy Efficiency"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="bb-body-section">
          <div className="bb-inner">
            <div className="bb-content-col">

              {/* italic opening quote — image 2 */}
              <p className="bb-quote bb-fade-up bb-delay-1">"Climate action does not require economic sacrifice," wrote Rachel Kyte, then CEO of the Sustainable Energy for All Initiative (SEforAll) in September 2015.</p>

              <p className="bb-para bb-fade-up bb-delay-1">Many studies and cases show that climate action does indeed yield economic benefits (see the case of China). Going even a step further: if climate change proceeds unchecked, its negative impacts will effect the world's economies at large. So in reality, without climate action there won't be much of any long-term economic activity anymore. To stay below the 1.5°C threshold agreed in the Paris Agreement means we need to drastically cut emissions. The recovery packages stipulated in the course of overcoming the impacts of the corona crisis can be an opportunity to channel needed investments into sustainable, green sectors and build back better.</p>

              {/* subhead — image 2 */}
              <h2 className="bb-subhead bb-fade-up bb-delay-1">Survive and Thrive!</h2>

              <p className="bb-para bb-fade-up bb-delay-1">The voices calling for a swift recovery and build back better from COVID are vast and from across the political spectrum. Yet, they often remain surprisingly vague on what "building back better" — or its variants stronger or greener — include.</p>

              <p className="bb-para bb-fade-up bb-delay-1">On the supply side, we need to significantly scale up renewable energy deployment. In recent years, governments, local authorities and businesses have taken up 100% RE targets. Their commitment emphasizes that a 100% RE future is not just possible, but also economically feasible. In fact, green energy has been more resilient against COVID-19 impacts than fossil fuels. They have experienced less supply chain disruptions than fossil fuels. Further, the wind and solar industries can create more jobs than their fossil counterparts. While the 100% RE vision has somewhat become mainstream in the renewable energy bubble, the importance of reducing demand for energy is still often overlooked.</p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}